import { faFilePdf, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { toXY } from "../../../util/geo";
import { MoreList } from "../../components/MoreList";
import { More, MoreFold } from "../../components/MoreToggle";
import { WozService, WozValues } from "../../services/wozService";
import { useAccount } from "../../state";
import { AddressDTO, Cube } from "../../state/types";
import { FetchState } from "../../state/util";
import AreaIcon from "../icons/AreaIcon.svg?react";
import BuildingIcon from "../icons/BuildingIcon.svg?react";
import CircumferenceIcon from "../icons/CircumferenceIcon.svg?react";
import { Section } from "./Section";
import { A, Info, KV, Links, Tooltip, fCoords, fa, rFormat } from "./util";

const agroCodes = ["311", "312", "313", "231", "211"];
const enc = encodeURIComponent;

export const IconText = ({ Icon, children, className = undefined }) => (
  <div className={className}>
    <div className="mx-auto bg-yellow/10 rounded-xl w-11 h-11 flex items-center justify-center text-gold-brown/50">
      <Icon fill="currentColor" className="w-7 h-7" />
    </div>
    {children}
  </div>
);
const description = (
  <>
    Hier staan de basis perceelsgegevens, inclusief gebouwen en adressen op dit perceel. Onderin
    links naar externe websites die niet geïntegreerd kunnen worden.
  </>
);

const Visual = ({ field }: { field: Cube }) => {
  const { area: builtUpArea, buildings } = field.builtUp;
  const { area, perimeter } = field.geometry.properties;
  // De gebruiksoppervlakte wonen plus overige inpandige ruimte volgens NEN 2580 met een minimale stahoogte van 1.5 meter.
  const buildingArea = field.addresses?.reduce((acc, a) => acc + a.area, 0) || 0;
  const w = buildingArea ? "w-1/3" : "w-2/5";

  return (
    <Info>
      <div className={`flex p-2.5 text-center`}>
        <IconText Icon={AreaIcon} className={w}>
          <div className="pt-3">Oppervlakte</div>
          <div className="text-lg font-bold">{rFormat.format(area)} m²</div>
        </IconText>
        <IconText Icon={CircumferenceIcon} className={w}>
          <div className="pt-3">Omtrek</div>
          <div className="text-lg font-bold">{rFormat.format(perimeter)} m</div>
        </IconText>
        {!builtUpArea ? null : (
          <IconText Icon={BuildingIcon} className={w}>
            <div className="pt-3">
              {buildings.length} {buildings.length === 1 ? "gebouw" : "gebouwen"}
            </div>
            <div>
              <span className="text-lg font-bold">{rFormat.format(builtUpArea)} m²</span>{" "}
              <Tooltip
                content={`De grondoppervlakte bedekt door gebouwen, (${rFormat.format(
                  (builtUpArea / area) * 100
                )}% van het kavel.`}
              >
                *
              </Tooltip>
            </div>
          </IconText>
        )}
      </div>
    </Info>
  );
};

const AddressLink = ({ address }) =>
  !address ? null : (
    <A
      href={`https://www.wozwaardeloket.nl/?locatie=${enc(address.zipCode + " " + address.number)}`}
    >
      WOZ waarde
    </A>
  );

// https://omgevingswet.overheid.nl/regels-op-de-kaart/documenten?locatie-stelsel=RD&locatie-x=106526&locatie-y=442422&bestuurslaag=gemeente
// https://omgevingswet.overheid.nl/regels-op-de-kaart/documenten?bestuurslaag=gemeente&locatie=sint-bonifaciusstraat-27-3812tc-amersfoort
const owURL =
  "https://omgevingswet.overheid.nl/regels-op-de-kaart/documenten?bestuurslaag=gemeente&";
function owsenc(s) {
  return enc(s.replaceAll(" ", "-"));
}
const OWLink = ({ address: a, xy }) => {
  const params = a
    ? `locatie=${owsenc(a.street + " " + a.number + " " + a.zipCode + " " + a.city)}`
    : `locatie-x=${xy[0].toFixed(0)}&locatie-y=${xy[1].toFixed(
        0
      )}&locatie-stelsel=RD&no-zoom=false`;
  return <A href={owURL + params}>Omgevingsloket</A>;
};

const MyLinks = ({ field }: { field: Cube }) => {
  const { user } = useAccount();
  const gps = field.center;
  const xy = toXY(gps);
  const address = field.addresses?.[0];
  const agro = field.data.landCover?.some((lc) => agroCodes.includes(lc.extId));
  const download = Boolean(user?.features?.download);

  return (
    <Links>
      <A href={`https://www.topotijdreis.nl/kaart/2022/@${fCoords(xy, ",", 0)},12`}>Topotijdreis</A>
      <AddressLink address={address} />
      <A href={`https://bgtviewer.nl/${fCoords(gps, "/")}/18`}>BGT</A>
      <OWLink address={address} xy={xy} />
      {agro && <A href={`https://boerenbunder.nl/report/${fCoords([gps[1], gps[0]], ",")}`}>B&B</A>}
      {download && (
        <A
          className="bg-transparent inline-block hover:underline text-sm aelement pr-0.5"
          href={`/report/parcel/${fCoords(gps, ",")}`}
          Icon={fa(faFilePdf)}
        >
          Rapport
        </A>
      )}
    </Links>
  );
};

const wozFormat = new Intl.NumberFormat("NL-nl", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

const WOZPrices = ({ address }: { address: AddressDTO }) => {
  const [state, setState] = useState<FetchState>("fetching");
  const [values, setValues] = useState<WozValues>({});

  useEffect(() => {
    let active = true;
    setState("fetching");
    WozService.values(address.extId)
      .then((data) => {
        if (!active) return;
        setValues(data);
        setState("fetched");
      })
      .catch(() => active && setState("error"));
    return () => {
      active = false;
    };
  }, [address.extId]);

  if (state === "fetching")
    return (
      <div className="text-dark-gray text-sm mt-1">
        <FontAwesomeIcon icon={faSpinner} spin={true} fill="green" /> WOZ-waarden laden…
      </div>
    );
  if (state === "error")
    return <div className="text-dark-gray text-sm mt-1">Kon WOZ-waarden niet laden.</div>;

  const years = Object.keys(values)
    .map(Number)
    .sort((a, b) => b - a);
  if (!years.length)
    return <div className="text-dark-gray text-sm mt-1">Geen WOZ-waarden bekend.</div>;

  return (
    <dl className="text-sm mt-1">
      {years.map((year) => (
        <div key={year} className="flex">
          <dt className="w-16 text-dark-gray">{year}</dt>
          <dd className="w-30 text-right font-mono">{wozFormat.format(values[year])}</dd>
        </div>
      ))}
    </dl>
  );
};

const Address = ({ address }: { address: AddressDTO }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="p-2.5">
      <More setOpen={setOpen} open={open} />
      {address.street} {address.number}, {address.zipCode} {address.city} ({address.area} m²)
      {open && <WOZPrices address={address} />}
    </div>
  );
};

const AddressesComponent = ({ data }: { data: AddressDTO[] }) => (
  <Info>
    {data.map((e, i) => (
      <Address key={i} address={e} />
    ))}
  </Info>
);

const Addresses = ({ addresses }: { addresses: AddressDTO[] }) => (
  <div>
    {addresses.length > 1 && (
      <div className="px-2.5 text-sm">
        <span className="font-semibold">{addresses.length}</span> adressen, gebruiksoppervlakte{" "}
        <Tooltip content="Het gaat hier om de gebruiksoppervlakte wonen plus overige inpandige ruimte volgens NEN 2580 met een minimale stahoogte van 1.5 meter.">
          <span className="font-semibold">
            {rFormat.format(addresses.reduce((acc, a) => acc + a.area, 0))} m²
          </span>{" "}
          *
        </Tooltip>
      </div>
    )}
    <MoreList data={addresses} Component={AddressesComponent} size={3}>
      Toon alle {addresses.length} adressen
    </MoreList>
  </div>
);

const Region = ({ data }) => {
  if (!data) return null;
  return (
    <KV k="Regio">
      {data.name}{" "}
      <MoreFold>
        {data.data.info} {data.data.link && <A href={data.data.link}>meer</A>}
      </MoreFold>
      {/* {adm.landscapeCharacter?.data?.info & .link} */}
    </KV>
  );
};

const Kadaster = ({ field }: { field: Cube }) => {
  const { name, names } = field;
  if (names?.length > 1) {
    const groups: Record<string, string[]> = names.reduce((acc, n) => {
      const parts = n.split(" ");
      const val = parts.pop();
      const key = parts.join("");
      if (!acc[key]) acc[key] = [];
      acc[key].push(val);
      return acc;
    }, {});
    return (
      <KV k="Kadaster">
        {Object.entries(groups).map(([k, vs]) => (
          <div key={k}>
            {k}: {vs.join(", ")}
          </div>
        ))}
      </KV>
    );
  }
  return <KV k="Kadaster">{name}</KV>;
};

const GPS = ({ gps }) => {
  const [rd, setRD] = useState(false);
  const coords = rd ? toXY(gps) : gps;
  const term = rd ? "RD" : "GPS";

  return (
    <KV k={`${term} coördinaten`} onClick={() => setRD(!rd)}>
      {fCoords(coords, ",", rd ? 0 : 5)}
    </KV>
  );
};

const Bouwjaar = ({ buildings }) => {
  if (!buildings.length) return null;
  const years = buildings.map((b) => b.year);
  const [minYear, maxYear] = [Math.min(...years), Math.max(...years)];

  return (
    <KV k="Bouwjaar">
      {minYear === maxYear ? (
        minYear
      ) : (
        <>
          `${minYear} - ${maxYear}`
        </>
      )}
    </KV>
  );
};

export const SectionBase = ({ field }: { field: Cube }) => {
  const [prov, mun, gps] = [field.administrative.zone1, field.administrative.zone2, field.center];
  const adm = field.administrative;
  const addresses = field.addresses;
  const status = [...new Set(field.builtUp.buildings.map((b) => b.status))];

  return (
    <Section name="base" description={description}>
      <Visual field={field} />
      <Info as="dl">
        <KV k="Gemeente" check={!!mun}>
          {mun?.name} ({prov.extId.substring(3)})
        </KV>
        <KV k="Buurt">
          {adm.zoneC1?.name} ({adm.zoneC2?.name})
        </KV>
        <Region data={adm.landscapeCharacter} />
        <Kadaster field={field} />
        <GPS gps={gps} />
        <KV k="Gebouw" check={status?.length > 0}>
          {status.join(", ")}
        </KV>
        <Bouwjaar buildings={field.builtUp.buildings} />
      </Info>

      <Addresses addresses={addresses} />
      {/* <BigButton>Kadastrale kaarten downloaden</BigButton> */}
      <MyLinks field={field} />
    </Section>
  );
};
